import type { Sanctuary, World } from './types';
import { civilFromEpochDay, epochDayFromCivil, epochDayOf } from './calendar';
import { STREAM, stockStream } from './streams';
import { FISH_LIVES, fishCounts, type FishSpeciesId } from './fish';
import { REEF_SPECIES } from './systems/fish';
import { reefCover } from './coral';
import { reefOf } from './data/sites';
import { sealCounts, MONK_SEAL } from './seals';
import { HONU, HONUEA, turtleCounts } from './turtles';
import type { Reef } from './reef';
import { held } from './held';
import { MANTA, MORAY, YELLOW_TANG } from './reefResidents';
import { WHITETIP_SHARK } from './whitetips';
import { REEF_SURVEYS } from './data/fish/reefSurveys';
import { NENUE_SURVEY } from './data/fish/nenueSurvey';
import { DEEP7, DEEP7_SPECIES, deep7BiomassKg, deep7Share, type Deep7SpeciesId } from './bottomfish';
import { SHRIMP_EXPANSION, SHRIMP_MAUI_NUI_KG } from './shrimp';
import type { SurveyKind } from './economy';

/**
 * What the monitoring programs know (M160, the user's choice "What is known": the player reads a population's size as
 * its monitoring knows it, never the model's own count). Each species has a program as its sources run it, in one of
 * the brief's four states:
 *
 * - **monitored**: counted again on a schedule, each count the model's number then with the program's own error drawn
 *   on it (a log-normal draw, its mean the true value, its coefficient of variation the program's), kept in
 *   `World.monitoring`, so the player's knowledge lags the reef by up to a survey's interval and is off by its error;
 * - **estimated**: one published figure with its interval, which the game does not re-run;
 * - **present**: known to live here, not counted (unknown is never zero);
 * - **not surveyed**.
 *
 * Every figure below is the reader's for M160, each quote checked against its source's text.
 */

/** One survey's result: the day it was taken, the program's estimate, and its coefficient of variation (none for a count). */
export interface SurveyResult {
  day: number;
  value: number;
  cv: number | null;
}

/** Each monitored species' results, oldest first. */
export type Monitoring = Record<string, SurveyResult[]>;

export type KnowledgeState = 'monitored' | 'estimated' | 'present' | 'not surveyed';

/** When a program counts: every `everyYears` years from `fromYear` on a month's day (month 0: NOAA's reef survey's, `NCRMP_DAY`). */
export interface Schedule {
  everyYears: number;
  fromYear: number;
  month: number;
  day: number;
}

export interface Program {
  state: KnowledgeState;
  /** What is counted, in its units, as the player reads it ("metric tons of fish", "seals", "% of the reef's floor"). */
  unit: string;
  /** Who counts it, how, and how often, with the source. */
  source: string;
  /** A monitored program's schedule, coefficient of variation (null: a count without error) and measure of the model. */
  schedule?: Schedule;
  cv?: (world: World) => number | null;
  measure?: (world: World) => number | null;
  /** Whether the world's first day takes a count (false where the program counts what is not there that day: the honu's breeders, away only in summer). */
  atOpening?: boolean;
  /** The year-to-year spread a count's trend must pass, as the log of the ratio's standard deviation, where its source gives one. */
  trendSigma?: number;
  /** An estimated species' published figure. */
  published?: { value: number; low: number | null; high: number | null; year: string; trend: string };
}

/** The reef fish surveys' and the coral's day: the middle of NOAA's 2024 survey at Maui Nui, every three years from it. */
function ncrmpDay(reef: Reef | null): { month: number; day: number } {
  const last = reef?.surveys.find((s) => s.year === 2024);
  // Maui Nui's reef table holds it; a table without it has no day to count on (review M162: a hand-set 1 August stood here).
  if (!last) throw new Error('no 2024 survey at Maui Nui to count the reef on');
  const c = civilFromEpochDay(last.medianEpochDay);
  return { month: c.month, day: c.day };
}

/** Maui Nui's, found once: every program counted on it (the reef and its fish) reads the Maui Nui sanctuary, so at another site it counts nothing, whatever its day. */
const NCRMP_DAY = ncrmpDay(reefOf({ siteId: 'maui_nui' }));

const mauiNui = (world: World): Sanctuary | undefined => world.sanctuaries.find((s) => s.siteId === 'maui_nui');

/** A migrant population's whole number, its females and its males. */
const migrantTotal = (w: { females: readonly number[]; males: readonly number[] }): number => w.females.reduce((a, b) => a + b, 0) + w.males.reduce((a, b) => a + b, 0);

/** A reef stock's weight at Maui Nui, tonnes: NOAA's divers' measure is the fish's weight over the hard bottom. */
const stockTonnes = (id: FishSpeciesId) => (world: World): number | null => {
  const s = mauiNui(world);
  const sp = REEF_SPECIES.find((x) => x.life.id === id);
  // None where the world holds no such stock: an older save's world on its way through a migration (M218, M220).
  if (!s?.fish?.[id] || !sp) return null;
  return fishCounts(sp.life, s.fish[id], epochDayOf(world)).kg / 1000;
};

/**
 * The reef-wide cover's coefficient of variation from the latest survey's strata: each stratum's standard error over
 * its floor's share (the strata independent), over the cover (MEASURED from the archived surveys, `MAUI_NUI_REEF`).
 */
export function coverCv(reef: Reef): number | null {
  const survey = reef.surveys.at(-1);
  if (!survey) return null;
  const area = (island: number, bin: number): number => {
    let km2 = 0;
    const of = bin === 0 ? reef.shallowKm2 : bin === 1 ? reef.midKm2 : reef.deepKm2;
    reef.island.forEach((i, k) => { if (i === island) km2 += of[k] as number; });
    return km2;
  };
  let mean = 0;
  let variance = 0;
  let all = 0;
  survey.strata.forEach((st, k) => {
    if (st.coverPercent === null || st.sePercent === null) return;
    const a = area(Math.floor(k / 3), k % 3);
    all += a;
    mean += a * st.coverPercent;
    variance += (a * st.sePercent) ** 2;
  });
  return all > 0 && mean > 0 ? Math.sqrt(variance) / mean : null;
}

/**
 * Nadon 2017's divers' biomass for the main islands, its SD (p. 169 and on: the sizes the fishery takes, 2005 to 2016),
 * over its median, which each life holds as `surveyTakenKg` (review M162: the medians were typed here again).
 */
const NADON_SD: Readonly<Partial<Record<FishSpeciesId, number>>> = {
  bullethead: 17_478,
  palenose: 41_926,
  redlip: 80_385,
  roi: 56_961,
  kala: 74_939,
  moanokea: 30_321,
  taape: 98_182,
};
/**
 * The herbivores' biomass's coefficient of variation in Nadon 2017's divers' survey of the main islands: the protected
 * herbivores it gives (the three parrotfish and the kala), their SDs summed in quadrature over their medians summed
 * (M274: a fish census of an island's herbivores is drawn with it, scaled by its sites, JUDGED: its other herbivores',
 * the nenue's and the manini's, taken as varying alike).
 */
export function herbivoreCv(): number {
  const ids: FishSpeciesId[] = ['bullethead', 'palenose', 'redlip', 'kala'];
  const sd = Math.sqrt(ids.reduce((a, id) => a + (NADON_SD[id] ?? 0) ** 2, 0));
  const median = ids.reduce((a, id) => a + (FISH_LIVES.find((l) => l.id === id)?.surveyTakenKg ?? 0), 0);
  return sd / median;
}

const NADON_CV: Readonly<Partial<Record<FishSpeciesId, number>>> = Object.fromEntries(
  FISH_LIVES.flatMap((l) => { const sd = NADON_SD[l.id]; return sd === undefined ? [] : [[l.id, sd / l.surveyTakenKg]]; }),
);

const NCRMP_FISH = (id: FishSpeciesId, page: number): Program => ({
  state: 'monitored',
  unit: 'metric tons of fish',
  source: `NOAA's divers (the National Coral Reef Monitoring Program) every three years on hard bottom to 30 m ("a triennial cycle", McCoy and Shantz 2026, p. 1, CC0), the error the main islands' (Nadon 2017, p. ${page})`,
  schedule: { everyYears: 3, fromYear: 2024, month: 0, day: 0 },
  cv: () => NADON_CV[id] ?? null,
  measure: stockTonnes(id),
});

/**
 * A Deep 7 species' survey (M192): NOAA's Bottomfish Fishery-Independent Survey in Hawaiʻi, each year in the fall, its
 * cameras and its research fishers across the main islands ("The 2024 BFISH was conducted from Aug 10 to November 24,
 * 2024", Richards 2025, p. 3), counted on 2 October, the middle of those days; what it sees the survey's share of the
 * assessed biomass (`DEEP7.surveyQ`), the species its share of the seven, with the 2024 survey's coefficient of
 * variation for it (Table 4, p. 12).
 */
const BFISH = (id: Deep7SpeciesId): Program => ({
  state: 'monitored',
  unit: 'metric tons of fish',
  source: 'NOAA\'s bottomfish survey (BFISH) each fall, by camera and research fishing on the deep slopes, the fish 29 cm and over (Richards 2025, NOAA PIFSC TM-177, pp. 3 and 12, CC0), the error its 2024 survey\'s; it sees about half the stock NOAA\'s assessment holds (Syslo et al. 2024, Table 12)',
  schedule: { everyYears: 1, fromYear: 2026, month: 10, day: 2 },
  cv: () => (DEEP7_SPECIES.find((s) => s.id === id) as (typeof DEEP7_SPECIES)[number]).cv,
  measure: (world) => { const s = mauiNui(world); return s?.slope ? (DEEP7.surveyQ * deep7BiomassKg(s.slope.deep7, epochDayOf(world)) * deep7Share(id)) / 1000 : null; },
});

/**
 * The deepwater shrimp's one estimate (M193): Maui Nui's stratum's exploitable biomass, trapped 1985 to 1989 and
 * expanded by its authors' catchability correction (`SHRIMP_MAUI_NUI_KG`), its 95% range from the stratum's variance,
 * "VAR[B]" 202,078,006 kg² (Ralston and Tagami 1992, Table 2, p. 500), expanded alike; none has counted it since.
 */
const SHRIMP_SE_KG = Math.sqrt(202_078_006) * SHRIMP_EXPANSION;

/** Every species' program, by its registry id (`species.ts`). */
export const PROGRAMS: Readonly<Record<string, Program>> = {
  coral: {
    state: 'monitored',
    unit: '% of the reef\'s floor live coral',
    source: 'NOAA\'s divers (the National Coral Reef Monitoring Program) every three years on hard bottom to 30 m (McCoy and Shantz 2026, p. 1, CC0), the error the latest survey\'s by stratum',
    schedule: { everyYears: 3, fromYear: 2024, month: 0, day: 0 },
    cv: (world) => { const s = mauiNui(world); const reef = s ? reefOf(s) : null; return reef ? coverCv(reef) : null; },
    measure: (world) => { const s = mauiNui(world); const reef = s ? reefOf(s) : null; return reef && s?.coral ? reefCover(reef, s.coral) : null; },
  },
  bullethead: NCRMP_FISH('bullethead', 169),
  palenose: NCRMP_FISH('palenose', 181),
  redlip: NCRMP_FISH('redlip', 187),
  roi: NCRMP_FISH('roi', 193),
  kala: NCRMP_FISH('kala', 69),
  ulua: { state: 'present', unit: 'fish', source: 'too few seen for NOAA\'s divers to count ("insufficient diver observations", Nadon 2017, p. 81); its catch-based estimate\'s error is several times itself' },
  omilu: { state: 'present', unit: 'fish', source: 'too fast for NOAA\'s divers to count ("the fast swimming behavior of this species made estimating population size unreliable", Nadon 2017, p. 88)' },
  octopus: { state: 'present', unit: 'octopus', source: 'no one counts it: the one count found 0.12 active dens per 100 m² of Kāneʻohe Bay\'s reef flats (Sims 1998, p. 10), searched in July and August (p. 11)' },
  honu: {
    state: 'monitored',
    unit: 'nesting females',
    source: 'Maui Nui\'s breeding females counted nesting at French Frigate Shoals each summer, as NOAA has counted the beaches since 1973 (Seminoff et al. 2015, p. 337), on 1 August, the end of the nesting peak (Niethammer et al. 1997, p. 38)',
    schedule: { everyYears: 1, fromYear: 2026, month: 8, day: 1 },
    atOpening: false,
    // "the average growth rate (r) for this annual nesting abundance time series is 0.048 and the standard deviation is
    // 0.142" (Seminoff et al. 2015, p. 338): a year's change within it is the nesting's own swing, not a trend.
    trendSigma: 0.142,
    cv: () => null,
    measure: (world) => { const s = mauiNui(world); return s?.turtles ? turtleCounts(HONU, s.turtles.honu, epochDayOf(world)).awayFemales : null; },
  },
  monkSeal: {
    state: 'monitored',
    unit: 'seals',
    source: 'counted each year by NOAA and its sighting network (PIFSC 2025, DR-25-03, CC0), the error the main islands\' (393, 95% interval 343 to 442: a coefficient of variation of 0.064)',
    schedule: { everyYears: 1, fromYear: 2026, month: 12, day: 31 },
    cv: () => (442 - 343) / (2 * 1.96 * 393),
    measure: (world) => { const s = mauiNui(world); return s?.seals ? sealCounts(MONK_SEAL, s.seals.monk, epochDayOf(world)).population : null; },
  },
  humpback: { state: 'estimated', unit: 'whales', source: 'NOAA\'s winter ship surveys, modeled (Young et al. 2026, p. 254, CC0)', published: { value: 11_278, low: 4_049, high: 31_412, year: '2020', trend: '"it is unknown if this population is currently increasing" (Young et al. 2026, p. 255)' } },
  spinnerDolphin: { state: 'present', unit: 'dolphins', source: 'no estimate of Maui Nui\'s alone: NOAA\'s stock assessment has "No minimum population estimate" for the Oʻahu/4-Islands stock, its last over eight years old (revised 3/1/2019, p. 4), its 1990s aerial count of 3,184 three stocks\' together, not to be split by island; Oʻahu\'s own counts, 594 the latest (Olson et al. 2026, p. 3), are Oʻahu\'s' },
  tigerShark: { state: 'present', unit: 'sharks', source: 'no one counts them: "there is also no reliable way of measuring tiger shark abundance around Maui" (Meyer et al. 2016, p. 3); the State\'s study followed 37 large ones in Maui\'s waters, 2013 to 2015 (pp. 1, 44 and 45)' },
  blueMarlin: { state: 'estimated', unit: 'metric tons of spawners', source: 'the ISC\'s stock assessment, every five years (ISC 2026, p. 41 and Table 7-2, p. 44)', published: { value: 42_038, low: null, high: null, year: '2021 to 2023', trend: 'spawners rising since 2017 (ISC 2026, Table 7-1)' } },
  leatherback: { state: 'estimated', unit: 'adult females', source: 'nests counted at the two Indonesian beaches, modeled (NMFS 2026, p. 27)', published: { value: 1_054, low: 888, high: 1_256, year: '2017', trend: '6.1% fewer a year from 2001 to 2017 (Martin et al. 2020, p. ix), "at least stable" since (NMFS 2026, p. 26)' } },
  rightWhale: {
    state: 'monitored',
    unit: 'whales',
    source: 'counted each year by photo-identification (Linden 2025, CC0: 384 at the start of 2024, 95% interval 375 to 394, a coefficient of variation of 0.013)',
    schedule: { everyYears: 1, fromYear: 2026, month: 1, day: 1 },
    cv: () => (394 - 375) / (2 * 1.96 * 384),
    measure: (world) => migrantTotal(world.migrants.rightWhale),
  },
  grayWhale: {
    state: 'monitored',
    unit: 'whales',
    source: 'counted from shore each winter, December to February (Lang, Eguchi and Weller 2026, CC0: a coefficient of variation of 0.0648)',
    schedule: { everyYears: 1, fromYear: 2026, month: 2, day: 28 },
    cv: () => 0.0648,
    measure: (world) => migrantTotal(world.migrants.grayWhale),
  },
  plankton: { state: 'monitored', unit: 'mg of chlorophyll a per cubic meter', source: 'seen from space every day (NASA\'s MODIS-Aqua, the plankton\'s check)' },
  // Last (M178): each program's place is its survey stream's (`MONITORED`), so a program added before another moves its draws.
  nenue: {
    ...NCRMP_FISH('nenue', 0),
    source: 'NOAA\'s divers (the National Coral Reef Monitoring Program) every three years on hard bottom to 30 m ("a triennial cycle", McCoy and Shantz 2026, p. 1, CC0), the error their own at Maui Nui, 2013 to 2024 (`scripts/nenue.ts`): no assessment counts the chubs',
    cv: () => NENUE_SURVEY.takenCv,
  },
  // Last (M179), as the nenue's above.
  moanokea: NCRMP_FISH('moanokea', 136),
  // Last (M187), as the nenue's above.
  honuea: {
    state: 'monitored',
    unit: 'nesting females',
    source: 'Maui Nui\'s hawksbills counted nesting each season, as Hawaiʻi Island\'s nesting beaches have been watched since 1989 and Maui\'s more intensely since 1996 (Gaos et al. 2021, p. 3), the count corrected for the watchers\' effort as Gaos et al. correct it (the field\'s own, "an annual average of 14", "likely underestimates", pp. 1 and 6), on 1 August, within the peak, "July (33.5%) and August (30.1%)" (p. 5)',
    schedule: { everyYears: 1, fromYear: 2026, month: 8, day: 1 },
    atOpening: false,
    // ESTIMATED: "a mean of 27.1 (±13.3) nesting females ... on Hawai'i Island each year" (Gaos et al. 2021, p. 5), the
    // spread of a year's count about the mean, its log's standard deviation √ln(1 + 0.491²); a year's change, between two
    // such years taken independent, √2 of it, 0.657. An upper bound: the spread holds the series' trend too, falling to
    // 2006 and rising 8.2% a year since (p. 6; M187's reviewer), so a trend the game reads is a large one. No year-to-year
    // series read (open).
    trendSigma: Math.SQRT2 * Math.sqrt(Math.log(1 + (13.3 / 27.1) ** 2)),
    // The corrected count taken as the count: its correction's error unread (open).
    cv: () => null,
    measure: (world) => { const s = mauiNui(world); return s?.turtles ? turtleCounts(HONUEA, s.turtles.honuea, epochDayOf(world)).awayFemales : null; },
  },
  // Last (M192), as the nenue's above, in the registry's order.
  ...Object.fromEntries(DEEP7_SPECIES.map((sp) => [sp.id, BFISH(sp.id)])),
  // Last (M193): estimated, no count of its own, so no survey's stream moves with it.
  deepwaterShrimp: {
    state: 'estimated',
    unit: 'metric tons of shrimp',
    source: 'NOAA\'s trapping survey of the main islands, 1985 to 1989, Maui Nui\'s stratum, its traps\' catchability corrected by its authors (Ralston and Tagami 1992, Fishery Bulletin, Table 2 and p. 502)',
    published: { value: Math.round(SHRIMP_MAUI_NUI_KG / 1000), low: Math.round((SHRIMP_MAUI_NUI_KG - 1.96 * SHRIMP_SE_KG) / 1000), high: Math.round((SHRIMP_MAUI_NUI_KG + 1.96 * SHRIMP_SE_KG) / 1000), year: '1985 to 1989', trend: 'not known: no survey since' },
  },
  // Last (M218, M220), as the nenue's above: the taʻape's error the main islands' (Nadon 2017, p. 109); the manini's none
  // the game holds (no assessment counts it, its density NOAA's divers' at Maui Nui, `REEF_SURVEYS`: open).
  taape: NCRMP_FISH('taape', 109),
  manini: {
    ...NCRMP_FISH('manini', 0),
    source: 'NOAA\'s divers (the National Coral Reef Monitoring Program) every three years on hard bottom to 30 m ("a triennial cycle", McCoy and Shantz 2026, p. 1, CC0); no assessment counts the manini, so no error is set on the count',
  },
  // Counted, not simulated (M217, M222, M223): no survey's stream moves with them.
  yellowTang: { state: 'present', unit: 'fish', source: `NOAA's divers count it every three years (McCoy and Shantz 2026, p. 1, CC0): about ${Math.round(YELLOW_TANG.perHa)} a hectare of the hard bottom of Maui Nui's reef, 2013 to 2024; the game does not yet simulate it` },
  moray: { state: 'present', unit: 'eels', source: `about ${Math.round(MORAY.cameraPerHa)} a hectare of the main islands' shallow reefs by eel-specific baited cameras (Zill 2024), most hidden in the reef's holes; NOAA's divers see about ${MORAY.perHa.toFixed(1)} a hectare of Maui Nui's hard bottom out in the open, 2013 to 2024` },
  manta: { state: 'present', unit: 'rays', source: `told apart by their spots in photographs: about ${MANTA.individualsSeen} at Maui Nui since 2005 (NOAA Fisheries, 2023), a floor, no estimate of their number` },
  whitetip: { state: 'present', unit: 'sharks', source: `NOAA's divers count it every three years: ${REEF_SURVEYS.whitetip.inCylinder} in their cylinders and ${REEF_SURVEYS.whitetip.seen} seen at ${REEF_SURVEYS.whitetip.visitsSeen} of ${REEF_SURVEYS.whitetip.visits} site visits, 2013 to 2024, too few to follow a trend; its life the Great Barrier Reef's, ${WHITETIP_SHARK.femaleMatureYears} years to maturity` },
};

/** Whether a schedule surveys in a year. */
const surveysIn = (sch: Schedule, year: number): boolean => year >= sch.fromYear && (year - sch.fromYear) % sch.everyYears === 0;

/** A schedule's survey day in a year. */
function surveyDayIn(sch: Schedule, year: number): number {
  const on = sch.month === 0 ? NCRMP_DAY : sch;
  return epochDayFromCivil(year, on.month, on.day);
}

/** A log-normal's variance of the log from its coefficient of variation, σ² = ln(1 + cv²), none for a count. */
export const logVariance = (cv: number | null): number => (cv === null ? 0 : Math.log(1 + cv * cv));

/** One survey's result: the model's value with the program's error drawn on it, on the program's own stream of the day. */
function survey(world: World, id: string, p: Program, index: number, day = epochDayOf(world), effort: { cvScale: number; salt: number } = { cvScale: 1, salt: STREAM.monitoring }): SurveyResult | null {
  const truth = p.measure?.(world) ?? null;
  if (truth === null) return null;
  const programCv = p.cv?.(world) ?? null;
  const cv = programCv === null ? null : programCv * effort.cvScale;
  // Held to six places, as every stored float is (`held`, review M76).
  if (cv === null || cv <= 0) return { day, value: held(truth), cv: null };
  // Log-normal, its mean the truth (`logVariance`).
  const sigma = Math.sqrt(logVariance(cv));
  const z = stockStream(world, effort.salt, id, index).normal();
  return { day, value: held(truth * Math.exp(sigma * z - (sigma * sigma) / 2)), cv: held(cv) };
}

/**
 * A program's last survey day on or before a day, within its year and the ten before it, or none: a yearly count's in the
 * years before its schedule's first too (the seals', the right whales' and the gray whales' counts ran before the
 * game's first year), the reef's three-yearly survey no earlier than NOAA's 2024 one, which its cycle is counted from.
 */
function lastSurveyDay(sch: Schedule, day: number): number | null {
  const year = civilFromEpochDay(day).year;
  for (let y = year; y >= year - 10; y--) {
    const d = sch.everyYears === 1 || surveysIn(sch, y) ? surveyDayIn(sch, y) : null;
    if (d !== null && d <= day) return d;
  }
  return null;
}

/** Each monitored program with its id and schedule, in `PROGRAMS`' order (its index the stream's place). */
const MONITORED = Object.entries(PROGRAMS).flatMap(([id, p]) => (p.schedule ? [{ id, p, sch: p.schedule }] : []));

/** The day's surveys: each program whose day it is counts, and its result is kept. */
export function stepMonitoring(world: World): void {
  const today = epochDayOf(world);
  const year = civilFromEpochDay(today).year;
  MONITORED.forEach(({ id, p, sch }, index) => {
    if (!surveysIn(sch, year) || surveyDayIn(sch, year) !== today) return;
    const r = survey(world, id, p, index);
    if (r) (world.monitoring[id] ??= []).push(r);
  });
}

/** One program's survey if a day is its day, as `stepMonitoring` takes it, else none (a v34 save's hawksbills' counts, M187). */
export function surveyOn(world: World, id: string): SurveyResult | null {
  const today = epochDayOf(world);
  const index = MONITORED.findIndex((m) => m.id === id);
  const m = MONITORED[index];
  if (!m || !surveysIn(m.sch, civilFromEpochDay(today).year) || surveyDayIn(m.sch, civilFromEpochDay(today).year) !== today) return null;
  return survey(world, id, m.p, index);
}

/**
 * A new world's knowledge: each monitored program's latest count on or before the world's first day, taken as the
 * opening state with the program's error (the world opens at its sources' measures, so its opening is what they last
 * counted), dated the day that program last counted (NOAA's reef survey of 2024 for a world opened in 2026, the seals'
 * of the last 31 December; review M160's reviewer: each was dated the world's first day).
 */
export function openingMonitoring(world: World): Monitoring {
  const out: Monitoring = {};
  const today = epochDayOf(world);
  MONITORED.forEach(({ id, p, sch }, index) => {
    if (p.atOpening === false) return;
    const r = survey(world, id, p, index, lastSurveyDay(sch, today) ?? today);
    if (r) out[id] = [r];
  });
  return out;
}

/**
 * The programs a survey the conservancy sends its own boat on counts (M238): the reef's fish as NOAA's divers count
 * them, the reef's coral as NOAA's photographs do, the Deep 7 as NOAA's cameras do; each kind's programs those its
 * habitat's program runs, so a commissioned count joins NOAA's in the player's knowledge.
 */
export const SURVEY_PROGRAMS: Readonly<Record<SurveyKind, readonly string[]>> = {
  reefFish: Object.keys(PROGRAMS).filter((id) => PROGRAMS[id]?.measure && PROGRAMS[id]?.unit === 'metric tons of fish' && !DEEP7_SPECIES.some((d) => d.id === id)),
  reefBenthic: ['coral'],
  // A bleaching assessment counts no program: what it finds is each area's (M274, `areas.ts`).
  reefBleaching: [],
  slopeCamera: DEEP7_SPECIES.map((d) => d.id),
  // A shore cleanup counts nothing: it clears a shore (M307, `shoreDebris.ts`).
  shoreCleanup: [],
};

/**
 * One program's count by a commissioned survey (M238), on the day it ends: the program's measure with its error
 * scaled by the survey's effort against the program's own (`cvScale`, `operations.ts`), drawn on the operations'
 * stream by the program and the survey's number, so it moves no scheduled count's draw.
 */
export function commissionedCount(world: World, id: string, cvScale: number, surveyId: number): SurveyResult | null {
  const p = PROGRAMS[id];
  return p ? survey(world, id, p, surveyId, epochDayOf(world), { cvScale, salt: STREAM.operations }) : null;
}

/**
 * The next day after a day that a monitoring program counts on its schedule, within three years, with the programs
 * counting that day (M240: the next event's), or none; only the programs `counted` keeps, where it is given.
 */
export function nextScheduledCount(after: number, counted: (id: string) => boolean = () => true): { day: number; ids: string[] } | null {
  const year = civilFromEpochDay(after).year;
  let best: { day: number; ids: string[] } | null = null;
  for (const { id, sch } of MONITORED) {
    if (!counted(id)) continue;
    for (let y = year; y <= year + 3; y++) {
      if (!surveysIn(sch, y)) continue;
      const d = surveyDayIn(sch, y);
      if (d <= after) continue;
      if (!best || d < best.day) best = { day: d, ids: [id] };
      else if (d === best.day) best.ids.push(id);
      break;
    }
  }
  return best;
}
